/**
 * Responsibility: describe the rolling hills biome — how tall its terrain is at any coordinate and what
 * colours it uses.
 *
 * Open country with real relief. It fills the gap between baseline, which is restful to the point of
 * being featureless, and the mountains, which are dramatic on purpose: there was nowhere in the world
 * that simply undulated.
 *
 * ## Plain fBm, like baseline and unlike mountains
 *
 * Ridged noise exists to make crests. Hills want the opposite — rounded, soft-shouldered, no sharp
 * ridgelines — so this is essentially baseline with the amplitude raised and the wavelength brought in.
 * The blobby look that makes plain fBm wrong for mountains is exactly right here.
 *
 * ## No structures
 *
 * Nothing is built or planted here. The interest is entirely in the shape of the ground, which makes this
 * the cheapest biome in the world to generate — one noise field per sample and no placement pass at all.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { createFbm, toUnitRange } from '../noise.js';
import { createTelescopeArray, createTelescopeClearance } from '../telescopeArray.js';
import { ROLLING_HILLS, PALETTE, TELESCOPE_PALETTE } from '../../config.js';

export const rollingHills = {
  id: 'hills',
  label: 'Rolling hills',

  palette: PALETTE.hills,

  /**
   * Dishes get their own cool steel-to-ice ramp rather than the hillside's violet.
   *
   * The whole point of an array is that it is *built*. Drawn in the ground's gradient it would read as an outcrop —
   * the same problem the forest solved by giving its trees a separate green.
   */
  structurePalette: TELESCOPE_PALETTE,

  suitabilityBias: ROLLING_HILLS.SUITABILITY_BIAS,

  /**
   * Build the elevation function for this biome.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number) => number} Elevation in world units, always >= 0.
   */
  createElevation(seed) {
    const field = createFbm(seed, 'elevation:hills', {
      octaves: ROLLING_HILLS.OCTAVES,
      baseFrequency: 1 / ROLLING_HILLS.WAVELENGTH,
      persistence: ROLLING_HILLS.PERSISTENCE,
      lacunarity: ROLLING_HILLS.LACUNARITY,
    });

    return function sampleHillsElevation(x, z) {
      /*
        Remapped to 0..1 so this biome never contributes a negative elevation. Blended elevation is a
        weighted sum, so a negative contribution would pull neighbouring terrain down through zero rather
        than simply flattening it.
      */
      const unit = toUnitRange(field(x, z));

      /*
        `CREST_SHAPE` below 1 lifts the mid-range, which keeps the ground on a slope for more of the
        flight instead of settling into flat valley floors between isolated bumps. Applied before scaling
        so the exponent shapes the *profile* of a hill rather than just its height.
      */
      return Math.pow(unit, ROLLING_HILLS.CREST_SHAPE) * ROLLING_HILLS.MAX_ELEVATION;
    };
  },

  /** Highest elevation this biome can produce, in world units. */
  maxElevation: ROLLING_HILLS.MAX_ELEVATION,

  /**
   * Build the structure placement function: radio telescope arrays.
   *
   * **The only host, deliberately.** An array needs level ground rather than a new kind of ground, so it is an
   * installation rather than a biome — and open ground would suit it just as well in principle. It is hosted here
   * alone because two biomes placing sites on independent grids could put two arrays on top of each other across a
   * transition, and one host makes that impossible rather than unlikely.
   *
   * Hills are a demanding host, which is the point: the site rules select the flat tops and valley floors of rolling
   * country, so an array turns up on a broad open shoulder rather than halfway down a slope.
   *
   * @param {number} seed - Base world seed.
   * @returns {(region: object, context: object) => Array<object>}
   */
  createStructures(seed) {
    return createTelescopeArray(seed, 'hills');
  },

  /**
   * Publish each site so other biomes keep out of it.
   *
   * Trees and rock formations both consult this. A spire growing through a dish is the tree-in-a-building bug in a
   * more expensive place, and unlike a tower a dish has nowhere to hide it.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number, radius: number, context: object) => boolean}
   */
  createReservation(seed) {
    return createTelescopeClearance(seed, 'hills');
  },
};
